import ChecklistRtlIcon from '@mui/icons-material/ChecklistRtl';

import {
  AccountBalanceWalletOutlinedIcon,
  BoltOutlined,
  DonutSmallOutlined,
  LoyaltyIconOutlined,
  ShoppingCartOutlinedIcon,
  LiveHelpOutlinedIcon,
  HubOutlinedIcon,
} from '@occc-app/design-system';
import config from '@occc/config';

import { ComponentType, memo } from 'react';
import { FormContentProps } from './FormContent';
import { CollapsibleCardContainer } from './CollapsibleCardContainer';
import { MemberAttributesFormContent } from './FuelAndConvenienceProfile/MemberAttributesFormContent';
import { PurchasesFormContent } from './Purchases/PurchasesFormContent';
import { FulfillmentFormContent } from './Fulfillment/FulfillmentFormContent';
import { SegmentsFormContent } from './Segments/SegmentsFormContent';
import { WalletSteeringFormContent } from './WalletSteering/WalletSteeringFormContent';
import { QualifiedEventsFormContent } from './QualifiedEvents/QualifiedEventsFormContent';
import { SurveysFormContent } from './Surveys/SurveysFormContent';
import { CombinedAudiencesFormContent } from './CombinedAudiences/CombinedAudiencesFormContent';

import { DynamicAudienceSection, sectionToFormFieldsMap } from '../constants';

export type RenderSectionProps = FormContentProps;

const WalletSteeringSection = () => {
  if (config.features.walletSteeringEnabled) {
    return (
      <CollapsibleCardContainer
        sectionName={DynamicAudienceSection.walletSteeringItems}
        fields={sectionToFormFieldsMap[DynamicAudienceSection.walletSteeringItems]}
        title='Wallets'
        key='Wallets'
        description='Target members Wallet Capacity or Wallet Opportunity'
        Icon={AccountBalanceWalletOutlinedIcon}
      >
        <WalletSteeringFormContent name='walletSteeringItems' />
      </CollapsibleCardContainer>
    );
  }

  return null;
};

const TransactionSection = () => {
  return (
    <CollapsibleCardContainer
      sectionName={DynamicAudienceSection.purchases}
      fields={sectionToFormFieldsMap[DynamicAudienceSection.purchases]}
      title='Transactions'
      key='Transactions'
      description='Use transaction data to filter your audience.'
      Icon={ShoppingCartOutlinedIcon}
    >
      <PurchasesFormContent name='purchases' />
    </CollapsibleCardContainer>
  );
};

const FulfillmentSection = () => {
  return (
    <CollapsibleCardContainer
      sectionName={DynamicAudienceSection.fulfillments}
      fields={sectionToFormFieldsMap[DynamicAudienceSection.fulfillments]}
      title='Fulfillment'
      key='Fulfillment'
      description='Use offer fulfillment to build an audience'
      Icon={LoyaltyIconOutlined}
    >
      <FulfillmentFormContent name='fulfillments' />
    </CollapsibleCardContainer>
  );
};

const MemberAttributesSection = (props: RenderSectionProps) => {
  const { clearFormField } = props;

  return (
    <CollapsibleCardContainer
      sectionName={DynamicAudienceSection.memberAttributes}
      fields={sectionToFormFieldsMap[DynamicAudienceSection.memberAttributes]}
      title='Member Attributes'
      key='Member Attributes'
      description='Target members based on their attributes such as age and verification status.'
      Icon={BoltOutlined}
    >
      <MemberAttributesFormContent
        name='behaviors'
        productInterestsFieldName='productInterests'
        lastVisitFieldName='lastVisit'
        pointBalanceFieldName='pointBalance'
        membershipCreatedFieldName='membershipCreated'
        clearFormField={clearFormField}
      />
    </CollapsibleCardContainer>
  );
};

const SegmentsSection = () => {
  return (
    <CollapsibleCardContainer
      sectionName={DynamicAudienceSection.segments}
      fields={sectionToFormFieldsMap[DynamicAudienceSection.segments]}
      title='Custom Segments'
      key='Custom Segments'
      description='Use transaction data to filter your audience.'
      Icon={DonutSmallOutlined}
    >
      <SegmentsFormContent name='segments' />
    </CollapsibleCardContainer>
  );
};

const QualifiedEventsSection = () => {
  return (
    <CollapsibleCardContainer
      sectionName={DynamicAudienceSection.qualifiedEvents}
      fields={sectionToFormFieldsMap[DynamicAudienceSection.qualifiedEvents]}
      title='Qualified Events'
      key='Qualified Events'
      description='Target members who have done certain actions a specific number of times.'
      Icon={ChecklistRtlIcon}
      infoText='A qualified event is only tracked after the audience is built.'
    >
      <QualifiedEventsFormContent name='qualifiedEvents' />
    </CollapsibleCardContainer>
  );
};

const SurveysSection = () => {
  return (
    <CollapsibleCardContainer
      sectionName={DynamicAudienceSection.surveys}
      fields={sectionToFormFieldsMap[DynamicAudienceSection.surveys]}
      title='Surveys'
      key='surveys'
      description='Target members based on their survey responses.'
      Icon={LiveHelpOutlinedIcon}
    >
      <SurveysFormContent name='surveys' />
    </CollapsibleCardContainer>
  );
};

const CombinedAudiencesSection = () => {
  return (
    <CollapsibleCardContainer
      sectionName={DynamicAudienceSection.combinedAudiences}
      fields={sectionToFormFieldsMap[DynamicAudienceSection.combinedAudiences]}
      title='Combined Audiences'
      key='combinedAudiences'
      description='Combine two or more existing audiences'
      Icon={HubOutlinedIcon}
      infoText="Note: this audience will not be available for 'survey not responded to' targeting."
    >
      <CombinedAudiencesFormContent name='combinedAudiences' />
    </CollapsibleCardContainer>
  );
};

export const sectionToComponentMap: Record<
  DynamicAudienceSection,
  ComponentType<RenderSectionProps>
> = {
  [DynamicAudienceSection.walletSteeringItems]: memo(WalletSteeringSection),
  [DynamicAudienceSection.purchases]: memo(TransactionSection),
  [DynamicAudienceSection.surveys]: memo(SurveysSection),
  [DynamicAudienceSection.fulfillments]: memo(FulfillmentSection),
  [DynamicAudienceSection.memberAttributes]: memo(MemberAttributesSection),
  [DynamicAudienceSection.combinedAudiences]: memo(CombinedAudiencesSection),
  [DynamicAudienceSection.segments]: memo(SegmentsSection),
  [DynamicAudienceSection.qualifiedEvents]: memo(QualifiedEventsSection),
};
